import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import React, { useEffect, useState } from 'react';
import { hasPermission } from '@/types';
import { employees as api, leaves as leavesApi } from '@/api/endpoints';
import type { LeaveOverview, LeaveRequest, LeaveMeta, LeaveTypeCode, LeaveStatusCode } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';

const pad2 = (n: number) => String(n).padStart(2, '0');
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
const fmtDays = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ','));
/** Poprawna forma po liczbie dni: „1 dzień", „2 dni", „0,5 dnia". */
const dni = (n: number) => `${fmtDays(n)} ${n === 1 ? 'dzień' : Number.isInteger(n) ? 'dni' : 'dnia'}`;

function openDownload(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Zakładka „Urlopy" na stronie pracownika: saldo (wymiar 20/26, zaległy, na żądanie, dni za święto
 * w sobotę), lista wpisów urlopowych oraz konfiguracja wymiaru/stażu. Zarządzanie centralne —
 * dodawać/edytować może OWNER/ADMIN/OFFICE; wymiar urlopu ustawia OWNER/ADMIN.
 */
export function UrlopyTab({ employeeId }: { employeeId: number }) {
  const { user } = useAuth();
  const canManage = hasPermission(user, 'HR_MANAGE');
  const canConfig = hasPermission(user, 'HR_SENSITIVE');

  const [year, setYear] = useState(new Date().getFullYear());
  const [ov, setOv] = useState<LeaveOverview | null>(null);
  const [meta, setMeta] = useState<LeaveMeta | null>(null);
  const [metaErr, setMetaErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [leaveModal, setLeaveModal] = useState<LeaveRequest | 'new' | null>(null);
  const [signModal, setSignModal] = useState<LeaveRequest | null>(null);
  const [entOpen, setEntOpen] = useState(false);

  const reload = () => api.leave.overview(employeeId, year).then(setOv).catch(e => setErr(e?.message ?? String(e)));
  useEffect(() => { setErr(null); reload(); /* eslint-disable-next-line */ }, [employeeId, year]);
  useEffect(() => { leavesApi.meta().then(setMeta).catch(e => setMetaErr('Nie udało się wczytać słowników urlopów: ' + (e?.message ?? String(e)))); }, []);

  const del = (r: LeaveRequest) => {
    if (!confirm(`Usunąć wpis ${r.leaveTypeName} ${r.startDate}–${r.endDate}?`)) return;
    api.leave.remove(employeeId, r.id).then(reload).catch(e => alert(e?.message ?? String(e)));
  };

  const unsign = (r: LeaveRequest) => {
    if (!confirm(`Cofnąć oznaczenie podpisanego wniosku (${r.leaveTypeName} ${r.startDate}–${r.endDate})?`)) return;
    api.leave.setSigned(employeeId, r.id, false).then(reload).catch(e => alert(e?.message ?? String(e)));
  };

  if (err) return <div className="text-red-500 text-[13px]">Błąd: {err}</div>;
  if (!ov) return <div className="text-ink-400">Ładowanie…</div>;
  const b = ov.balance;

  return (
    <div className="flex flex-col gap-4">
      {/* Nawigacja rok + akcje */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center rounded-md border border-ink-200 overflow-hidden">
          <button onClick={() => setYear(year - 1)} className="px-2 py-1.5 hover:bg-ink-50" title="Poprzedni rok"><Icon name="chevron" className="w-4 h-4 rotate-90"/></button>
          <span className="px-3 py-1.5 text-[13px] font-semibold text-ink-900 min-w-[64px] text-center">{year}</span>
          <button onClick={() => setYear(year + 1)} className="px-2 py-1.5 hover:bg-ink-50 border-l border-ink-200" title="Następny rok"><Icon name="chevron" className="w-4 h-4 -rotate-90"/></button>
        </div>
        <div className="ml-auto flex gap-2">
          {canConfig && (
            <button onClick={() => setEntOpen(true)} className="btn btn-secondary btn-sm">
              <Icon name="edit" className="w-3.5 h-3.5"/> Wymiar urlopu
            </button>
          )}
          {canManage && (
            <button onClick={() => setLeaveModal('new')} disabled={!meta} title={meta ? '' : 'Ładowanie słowników…'} className="btn btn-primary btn-sm disabled:opacity-50">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj urlop
            </button>
          )}
        </div>
      </div>
      {metaErr && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{metaErr}</div>}

      {/* Saldo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Tile label="Wymiar" value={fmtDays(b.annualDays)}
              hint={b.annualOverridden ? `nadpisany (auto ${fmtDays(b.annualDaysAuto)})` : `auto wg stażu${b.seniorityYears != null ? ` ${b.seniorityYears} lat` : ''}`}/>
        <Tile label="Zaległy" value={fmtDays(b.carriedOverDays)} hint="z poprz. roku"/>
        <Tile label="Wykorzystano" value={fmtDays(b.usedVacationDays)} hint="wypoczynkowy + na żądanie"/>
        <Tile label="Pozostało" value={fmtDays(b.remainingVacationDays)} accent={b.remainingVacationDays < 0} strong hint="wymiar + zaległy − wykorzystano"/>
        <Tile label="Na żądanie" value={`${fmtDays(b.onDemandUsedDays)}/${b.onDemandLimit}`} hint="limit 4 dni/rok"/>
        <Tile label="Dni za święto" value={`${fmtDays(b.saturdayHolidayUsedDays)}/${b.saturdayHolidayDays}`} hint="święta w sobotę"/>
      </div>
      {b.saturdayHolidayDays > 0 && (
        <div className="text-[11px] text-ink-500 -mt-2">
          <Icon name="calendar" className="w-3 h-3 inline -mt-0.5 mr-1"/>
          W {year} r. przypada <strong>{b.saturdayHolidayDays}</strong> {b.saturdayHolidayDays === 1 ? 'święto' : 'święta'} w sobotę — pracownikowi należy się tyle dodatkowych dni wolnych (art. 130 §2 KP).
        </div>
      )}

      {/* Lista wpisów */}
      <div className="rounded-lg border border-ink-100 overflow-hidden">
        <table className="table-hw">
          <thead>
            <tr>
              <th>Typ</th><th>Od</th><th>Do</th><th className="text-right">Dni robocze</th><th>Status</th><th>Wniosek</th><th>Uwagi</th><th></th>
            </tr>
          </thead>
          <tbody>
            {ov.requests.length === 0 && (
              <tr><td colSpan={8} className="text-center text-ink-400 py-4">Brak wpisów urlopowych w {year} r.</td></tr>
            )}
            {ov.requests.map(r => (
              <tr key={r.id} className="hover:bg-ink-50">
                <td><span className={'pill pill-' + r.color}>{r.leaveTypeName}</span></td>
                <td className="font-mono text-[12px] text-ink-700">{r.startDate}</td>
                <td className="font-mono text-[12px] text-ink-700">{r.endDate}{r.halfDay && ' ½'}</td>
                <td className="text-right font-mono text-[13px] text-ink-900">{fmtDays(r.workingDays)}</td>
                <td><span className={'pill pill-' + r.statusColor}>{r.statusName}</span></td>
                <td className="whitespace-nowrap">
                  {r.signedAt ? (
                    <button onClick={() => canManage && unsign(r)} disabled={!canManage}
                            title={canManage ? 'Cofnij oznaczenie podpisu' : `Wniosek podpisany ${r.signedAt}`}
                            className={canManage ? 'cursor-pointer' : 'cursor-default'}>
                      <span className="pill pill-green">Podpisany {r.signedAt}</span>
                    </button>
                  ) : canManage ? (
                    <button onClick={() => setSignModal(r)} className="btn btn-ghost btn-sm text-ink-400 hover:text-green-600" title="Oznacz, że pracownik podpisał wniosek">
                      <Icon name="check" className="w-3.5 h-3.5"/> Oznacz podpisany
                    </button>
                  ) : <span className="text-ink-300">—</span>}
                </td>
                <td className="text-[12px] text-ink-500 truncate max-w-[220px]">{r.note}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => openDownload(api.leave.wniosekPdfUrl(employeeId, r.id))}
                          className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600" title="Pobierz wniosek urlopowy (PDF) do wydruku i podpisu">
                    <Icon name="download" className="w-3.5 h-3.5"/>
                  </button>
                  {canManage && <button onClick={() => setLeaveModal(r)} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600" title="Edytuj"><Icon name="edit" className="w-3.5 h-3.5"/></button>}
                  {canManage && <button onClick={() => del(r)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Usuń"><Icon name="x" className="w-3.5 h-3.5"/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {leaveModal && meta && (
        <LeaveModal employeeId={employeeId} item={leaveModal === 'new' ? null : leaveModal} meta={meta}
                    onClose={() => setLeaveModal(null)} onSaved={() => { setLeaveModal(null); reload(); }}/>
      )}
      {signModal && (
        <SignModal employeeId={employeeId} item={signModal}
                   onClose={() => setSignModal(null)} onSaved={() => { setSignModal(null); reload(); }}/>
      )}
      {entOpen && (
        <EntitlementModal employeeId={employeeId} year={year} balance={b}
                          onClose={() => setEntOpen(false)} onSaved={() => { setEntOpen(false); reload(); }}/>
      )}
    </div>
  );
}

function Tile({ label, value, hint, accent, strong }: { label: string; value: string; hint?: string; accent?: boolean; strong?: boolean }) {
  return (
    <div className={'rounded-md border px-3 py-2 ' + (strong ? 'border-water-200 bg-water-500/5' : 'border-ink-100')}>
      <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">{label}</div>
      <div className={'text-[18px] font-semibold font-mono ' + (accent ? 'text-red-600' : 'text-ink-900')}>{value}</div>
      {hint && <div className="text-[10px] text-ink-400 leading-tight mt-0.5">{hint}</div>}
    </div>
  );
}

function LeaveModal({ employeeId, item, meta, onClose, onSaved }: {
  employeeId: number; item: LeaveRequest | null; meta: LeaveMeta;
  onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!item;
  const [leaveType, setLeaveType] = useState<LeaveTypeCode>(item?.leaveType ?? 'WYPOCZYNKOWY');
  const [status, setStatus] = useState<LeaveStatusCode>(item?.status ?? 'APPROVED');
  const [startDate, setStartDate] = useState(item?.startDate ?? todayIso());
  const [endDate, setEndDate] = useState(item?.endDate ?? item?.startDate ?? todayIso());
  const [halfDay, setHalfDay] = useState(item?.halfDay ?? false);
  const [note, setNote] = useState(item?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const singleDay = startDate === endDate;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) { setErr('Podaj zakres dat'); return; }
    if (endDate < startDate) { setErr('Data zakończenia nie może być wcześniejsza niż rozpoczęcia'); return; }
    setBusy(true); setErr(null);
    const data = { leaveType, status, startDate, endDate, halfDay: singleDay && halfDay, note: note.trim() || null };
    const op = isEdit ? api.leave.update(employeeId, item!.id, data) : api.leave.add(employeeId, data);
    op.then(onSaved).catch(e2 => { setErr(e2?.message ?? String(e2)); setBusy(false); });
  };

  return (
    <Shell title={isEdit ? 'Edytuj urlop' : 'Nowy urlop / nieobecność'} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ leaveType, status, startDate, endDate, halfDay, note }}>
      <label className="field">
        <span className="field-label">Typ *</span>
        <SearchableSelect value={leaveType} onChange={v => setLeaveType(v as LeaveTypeCode)}>
          {meta.leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </SearchableSelect>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="field">
          <span className="field-label">Od *</span>
          <input type="date" required value={startDate} onChange={e => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} className="input"/>
        </label>
        <label className="field">
          <span className="field-label">Do *</span>
          <input type="date" required value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)} className="input"/>
        </label>
      </div>
      {singleDay && (
        <label className="flex items-center gap-2 text-[13px] text-ink-700">
          <input type="checkbox" checked={halfDay} onChange={e => setHalfDay(e.target.checked)}/> Pół dnia (0,5)
        </label>
      )}
      <label className="field">
        <span className="field-label">Status</span>
        <SearchableSelect value={status} onChange={v => setStatus(v as LeaveStatusCode)}>
          {meta.leaveStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SearchableSelect>
      </label>
      <label className="field">
        <span className="field-label">Uwagi</span>
        <textarea value={note} onChange={e => setNote(e.target.value)} className="textarea" rows={2}/>
      </label>
      <div className="text-[11px] text-ink-400">Liczba dni roboczych (bez weekendów i świąt) policzy się automatycznie po zapisie.</div>
    </Shell>
  );
}

/** Oznaczenie odebranego, podpisanego przez pracownika wniosku urlopowego (data podpisu). */
function SignModal({ employeeId, item, onClose, onSaved }: {
  employeeId: number; item: LeaveRequest; onClose: () => void; onSaved: () => void;
}) {
  const [signedAt, setSignedAt] = useState(item.signedAt ?? todayIso());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    api.leave.setSigned(employeeId, item.id, true, signedAt)
      .then(onSaved).catch(e2 => { setErr(e2?.message ?? String(e2)); setBusy(false); });
  };

  return (
    <Shell title="Podpisany wniosek urlopowy" onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ signedAt }}>
      <div className="text-[13px] text-ink-700">
        {item.leaveTypeName} · <span className="font-mono">{item.startDate}–{item.endDate}</span> ({dni(item.workingDays)})
      </div>
      <label className="field">
        <span className="field-label">Data podpisania wniosku *</span>
        <input type="date" required max={todayIso()} value={signedAt} onChange={e => setSignedAt(e.target.value)} className="input"/>
        <span className="text-[11px] text-ink-400 mt-0.5">Zaznacz po odebraniu papierowego wniosku podpisanego przez pracownika.</span>
      </label>
      <div className="text-[11px] text-ink-400">
        Uwaga: zmiana typu lub terminu wpisu skasuje to oznaczenie — podpisany wniosek dotyczy konkretnego terminu.
      </div>
    </Shell>
  );
}

function EntitlementModal({ employeeId, year, balance, onClose, onSaved }: {
  employeeId: number; year: number; balance: { seniorityYears: number | null; annualDays: number; annualDaysAuto: number; annualOverridden: boolean; carriedOverDays: number };
  onClose: () => void; onSaved: () => void;
}) {
  const [seniority, setSeniority] = useState<string>(balance.seniorityYears != null ? String(balance.seniorityYears) : '');
  const [annual, setAnnual] = useState<string>(balance.annualOverridden ? String(balance.annualDays) : '');
  const [carried, setCarried] = useState<string>(String(balance.carriedOverDays));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    api.leave.updateEntitlement(employeeId, year, {
      seniorityYears: seniority === '' ? null : Number(seniority),
      annualDays: annual === '' ? null : Number(annual),
      carriedOverDays: carried === '' ? 0 : Number(carried),
    }).then(onSaved).catch(e2 => { setErr(e2?.message ?? String(e2)); setBusy(false); });
  };

  return (
    <Shell title={`Wymiar urlopu ${year}`} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ seniority, annual, carried }}>
      <label className="field">
        <span className="field-label">Staż pracy (lata, ze stażem z wykształcenia)</span>
        <input type="number" min="0" value={seniority} onChange={e => setSeniority(e.target.value)} className="input" placeholder="np. 12"/>
        <span className="text-[11px] text-ink-400 mt-0.5">≥ 10 lat → 26 dni, poniżej → 20 dni (× wymiar etatu).</span>
      </label>
      <label className="field">
        <span className="field-label">Wymiar dni (nadpisanie)</span>
        <input type="number" min="0" step="0.5" value={annual} onChange={e => setAnnual(e.target.value)} className="input"
               placeholder={`auto: ${fmtDays(balance.annualDaysAuto)}`}/>
        <span className="text-[11px] text-ink-400 mt-0.5">Puste = wyliczany automatycznie ({fmtDays(balance.annualDaysAuto)} dni).</span>
      </label>
      <label className="field">
        <span className="field-label">Urlop zaległy (z poprzedniego roku)</span>
        <input type="number" min="0" step="0.5" value={carried} onChange={e => setCarried(e.target.value)} className="input"/>
      </label>
    </Shell>
  );
}

/** Lokalna kopia modala (jak w EwidencjaTab) — nie importujemy ModalShell z KadryDetailPage (cykl importów). */
function Shell({ title, onClose, onSubmit, busy, err, children, draftValue }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void;
  busy: boolean; err: string | null; children: React.ReactNode; draftValue: unknown;
}) {
  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: draftValue, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={onSubmit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[560px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">{title}</h2></div>
        <div className="p-5 grid gap-3">
          {children}
          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary"><Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
